import Dialog from "@mui/material/Dialog"
import Drawer from "@mui/material/Drawer"
import { AsYouType, isValidPhoneNumber } from "libphonenumber-js"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { useDebounceValue, useScript } from "usehooks-ts"

import { useAuthorizedAxiosClient } from "../../common/axios"
import { useCloseableSnackbarState } from "../../common/components/CloseableSnackbar"
import { usePaginationState } from "../../common/components/tables/utils"
import SegmentTracker from "../../common/tracking"
import { useIsMobile } from "../../common/utils"
import { isConcierge } from "../users/accountRole"
import { promptDailyReportSubmission } from "../users/dailyReportPrompt"
import { openSaveSessionNotesModal } from "../users/openSaveSessionNotesModal"
import { removeToken, selectFullName, selectProfile } from "../users/reducer"
import {
  A2P_10DLC_REG_STAGES,
  getSmsSendingPrerequisiteState,
} from "./a2p/a2pStatus"

export { A2P_10DLC_REG_STAGES, getSmsSendingPrerequisiteState }

export const PROFILE_PHONE_INVALID_MESSAGE = "Invalid phone number"

/** Same rules as Settings → Profile phone (`isValidPhoneNumber` for US). */
export function validateUSPhoneNumber(value, { required = false } = {}) {
  const raw = String(value ?? "").trim()
  if (!raw) {
    return {
      valid: !required,
      message: required ? "Required" : "",
    }
  }
  if (isValidPhoneNumber(raw, "US")) {
    return { valid: true, message: "" }
  }
  return { valid: false, message: PROFILE_PHONE_INVALID_MESSAGE }
}

export function useValidatedPhoneNumber(asYouTypeFormat = false) {
  const [phone, setPhone] = useState("")
  const [phoneError, setPhoneError] = useState(null)

  function handlePhoneChanged(newValue) {
    const check = validateUSPhoneNumber(newValue)
    setPhoneError(check.valid ? null : check.message)
    setPhone(asYouTypeFormat ? new AsYouType("US").input(newValue) : newValue)
  }

  return {
    phone,
    setPhone,
    phoneError,
    handlePhoneChanged,
  }
}

export function commissionRatioDisplay(commissionTiers) {
  if (!commissionTiers?.length) {
    return ""
  }
  const brokerSplit = commissionTiers[0]["broker_split"]
  return `${brokerSplit}/${100 - brokerSplit}`
}

export function useUpdateCreateCampaignDialogState() {
  const [updateCreateCamaignDialogOpen, setUpdateCreateCamaignDialogOpen] =
    useState(false)
  const [campaignBeingEdited, setCampaignBeingEdited] = useState(null)
  const [campaigns, setCampaigns] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const {
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    setTotalResultsSize,
  } = usePaginationState()
  const {
    open: openSnackbar,
    openError: openSnackbarError,
    isOpen: isSnackbarOpen,
    close: closeSnackbar,
    message: snackbarMessage,
    severity: snackbarSeverity,
  } = useCloseableSnackbarState()

  function openUpdateCreateCampaignDialog(campaign = null) {
    setUpdateCreateCamaignDialogOpen(true)
    setCampaignBeingEdited(campaign)
  }

  function closeUpdateCreateCampaignDialog() {
    setUpdateCreateCamaignDialogOpen(false)
    setCampaignBeingEdited(null)
  }

  function handleUpdateCreateDialogClosed(
    updatedOrNewCampaign,
    campaignOperation
  ) {
    switch (campaignOperation) {
      case CAMPAIGN_OPERATIONS.created:
        setCampaigns([updatedOrNewCampaign].concat(campaigns))
        openSnackbar("Campaign created successfully")
        setTotalResultsSize(totalResultsSize + 1)
        break
      case CAMPAIGN_OPERATIONS.deleted:
        setCampaigns(
          campaigns.filter(
            (campaign) => campaign.id !== updatedOrNewCampaign.id
          )
        )
        openSnackbar("Campaign deleted successfully")
        setTotalResultsSize(totalResultsSize - 1)
        break
      case CAMPAIGN_OPERATIONS.updated:
        setCampaigns(
          campaigns.map((campaign) =>
            campaign.id === updatedOrNewCampaign.id
              ? updatedOrNewCampaign
              : campaign
          )
        )
        openSnackbar("Campaign updated successfully")
        break
      default:
        break
    }
    closeUpdateCreateCampaignDialog()
  }

  return {
    updateCreateCamaignDialogOpen,
    campaignBeingEdited,
    openUpdateCreateCampaignDialog,
    handleUpdateCreateDialogClosed,
    campaigns,
    setCampaigns,
    isLoading,
    setIsLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    setTotalResultsSize,
    openSnackbar,
    openSnackbarError,
    isSnackbarOpen,
    closeSnackbar,
    snackbarMessage,
    snackbarSeverity,
  }
}

export function addCRMEventToCandidate(setCandidate, crmEvent, eventType) {
  setCandidate((candidate) => ({
    ...candidate,
    crm_events: [...candidate.crm_events, { ...crmEvent, type: eventType }],
  }))
}

export function upsertCRMEventToCandidate(setCandidate, crmEvent, eventType) {
  setCandidate((candidate) => {
    const typedEvent = { ...crmEvent, type: eventType }
    const existingIndex = candidate.crm_events.findIndex(
      (event) => event.type === eventType && event.id === crmEvent.id
    )
    if (existingIndex === -1) {
      return {
        ...candidate,
        crm_events: [...candidate.crm_events, typedEvent],
      }
    }
    return {
      ...candidate,
      crm_events: candidate.crm_events.map((event, index) =>
        index === existingIndex ? typedEvent : event
      ),
    }
  })
}

export function removeCRMEventFromCandidate(setCandidate, crmEvent, eventType) {
  setCandidate((candidate) => ({
    ...candidate,
    crm_events: candidate.crm_events.filter(
      (event) => !(event.type === eventType && event.id === crmEvent.id)
    ),
  }))
}

export function updateCRMEventForCandidate(setCandidate, crmEvent, eventType) {
  setCandidate((candidate) => ({
    ...candidate,
    crm_events: candidate.crm_events.map((event) =>
      event.id === crmEvent.id ? { ...crmEvent, type: eventType } : event
    ),
  }))
}

export function useLogoutUser() {
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()
  const profile = useSelector(selectProfile)

  return async () => {
    if (isConcierge(profile)) {
      try {
        const { data } = await axios.get(
          "/clients/api/concierge/daily-activity/logout-prompt/"
        )
        const prompt = data?.daily_report_prompt
        if (prompt?.should_prompt) {
          const openReport = await promptDailyReportSubmission(
            prompt.client_name
          )
          if (openReport) {
            await openSaveSessionNotesModal({
              axios,
              reportDate: prompt.report_date,
              accountOwnerId: prompt.account_owner_id,
            })
          }
        }
      } catch (_error) {
        // Logout should still work if the prompt endpoint is unavailable.
      }
    }
    await dispatch(removeToken())
    SegmentTracker.logout()
  }
}

export function useDialogContainer() {
  const isMobile = useIsMobile()
  return isMobile ? Dialog : Drawer
}

export function useGetTransactionInfo() {
  const userName = useSelector(selectFullName)

  function getSenderReceiverNames(transaction, candidate) {
    let senderName = userName,
      receiverName = candidate.candidate_name
    if (transaction.direction === TRANSACTION_DIRECTIONS.incoming) {
      senderName = candidate.candidate_name
      receiverName = userName
    }
    return { senderName, receiverName }
  }

  return {
    getSenderReceiverNames,
  }
}

export function useScrollToTargetElementRef(object, urlParamName) {
  const searchParams = useSearchParams()[0]
  const elemRef = useRef()

  useEffect(
    () => {
      if (!elemRef.current) {
        return
      }
      const objectID = searchParams.get(urlParamName)
      if (objectID && object.id === parseInt(objectID)) {
        elemRef.current.scrollIntoView()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, elemRef.current]
  )

  return elemRef
}

export function useGoogleAddressAutocomplete(address) {
  const [addressOptions, setAddressOptions] = useState([])
  const debouncedAddressInputValue = useDebounceValue(address)[0]
  const googlePlacesScriptStatus = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`
  )
  const googleAutocompleteService = useRef()

  useEffect(() => {
    if (googlePlacesScriptStatus !== "ready" || !window?.google?.maps?.places) {
      return
    }
    googleAutocompleteService.current =
      new window.google.maps.places.AutocompleteService()
  }, [googlePlacesScriptStatus])

  useEffect(() => {
    if (!googleAutocompleteService.current) {
      return
    }
    if (debouncedAddressInputValue) {
      googleAutocompleteService.current.getQueryPredictions(
        { input: debouncedAddressInputValue },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
            return
          }
          setAddressOptions(predictions.map((p) => p.description))
        }
      )
    } else {
      setAddressOptions([])
    }
  }, [debouncedAddressInputValue, googleAutocompleteService])

  return {
    addressOptions,
    setAddressOptions,
  }
}

export function validateURL(url) {
  if (!url) {
    return true
  }

  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function validateSocialLink(link) {
  // make sure they don't enter an actual URL
  try {
    new URL(link)
    return false
  } catch {
    return true
  }
}

export function useValidateMaxValueErrorState(maxValue) {
  const [error, setError] = useState(null)

  function validateMaxValue(newValue) {
    if (newValue < 0) {
      setError("Value must be positive")
    } else if (newValue > maxValue) {
      setError(`Value cannot be larger than ${maxValue.toLocaleString()}`)
    } else {
      setError(null)
    }
  }

  return {
    validateMaxValue,
    error,
    fieldProps: {
      error: !!error,
      helperText: error,
    },
  }
}

export function useCustomPerksState() {
  const [customPerks, setCustomPerks] = useState([])
  const [customPerkDialogOpen, setCustomPerkDialogOpen] = useState(false)
  const [editedPerk, setEditedPerk] = useState(null)
  const axios = useAuthorizedAxiosClient()

  function openCustomPerkDialog() {
    setCustomPerkDialogOpen(true)
  }

  function handleCustomPerkDialogClosed(newPerk) {
    if (newPerk) {
      if (editedPerk) {
        setCustomPerks(
          customPerks.map((perk) =>
            perk.id === editedPerk.id ? newPerk : perk
          )
        )
      } else {
        setCustomPerks([...customPerks, newPerk])
      }
    }
    setEditedPerk(null)
    setCustomPerkDialogOpen(false)
  }

  useEffect(
    () => {
      axios
        .get("/crm/api/custom-perk/")
        .then(({ data }) => setCustomPerks(data))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return {
    customPerks,
    setCustomPerks,
    customPerkDialogOpen,
    openCustomPerkDialog,
    handleCustomPerkDialogClosed,
    editedPerk,
    setEditedPerk,
  }
}

export function useLoadCRMStages() {
  const [crmStages, setCrmStages] = useState([])
  const [isLoadingCRMStages, setIsLoadingCRMStages] = useState(false)
  const axios = useAuthorizedAxiosClient()

  function loadCRMStages() {
    setIsLoadingCRMStages(true)
    return axios
      .get("/crm/api/crm-stages/candidate/")
      .then(({ data }) => {
        setCrmStages(data)
        return Promise.resolve(data)
      })
      .finally(() => setIsLoadingCRMStages(false))
  }

  return {
    crmStages,
    isLoadingCRMStages,
    loadCRMStages,
  }
}

export function getFileExtension(fileName) {
  const filenameParts = fileName.split(".")
  return filenameParts[filenameParts.length - 1]
}

export function isCheckboxClickEvent(e) {
  const { target } = e
  if (!(target instanceof Element)) {
    return false
  }
  const tag = target.tagName.toLowerCase()
  if (tag === "label" || tag === "input") {
    return true
  }
  // MUI Checkbox: clicks often land on svg/path/span, not the hidden input.
  return target.closest(".MuiCheckbox-root") != null
}

export function isTaskOverdue(task) {
  return !task.completed && new Date(task.due_datetime) < new Date()
}

export const CANDIDATE_MAX_STATS = {
  salesVolume: 2e9,
  homesSold: 1000,
  yearsInBusiness: 100,
}

export const AUTOCOMPLETE_OPTIONS = [
  {
    label: "Candidate first name",
    fieldName: "first_name",
  },
  {
    label: "Candidate last name",
    fieldName: "last_name",
  },
  {
    label: "Candidate full name",
    fieldName: "full_name",
  },
  {
    label: "Candidate company",
    fieldName: "current_brokerage",
  },
  {
    label: "User first name",
    fieldName: "user_first_name",
  },
  {
    label: "User last name",
    fieldName: "user_last_name",
  },
  {
    label: "User full name",
    fieldName: "user_full_name",
  },
  {
    label: "Broker first name",
    fieldName: "broker_first_name",
  },
  {
    label: "Broker last name",
    fieldName: "broker_last_name",
  },
  {
    label: "Broker full name",
    fieldName: "broker_full_name",
  },
  {
    label: "Broker title",
    fieldName: "broker_title",
  },
  {
    label: "Broker phone",
    fieldName: "broker_phone",
  },
  {
    label: "Office name",
    fieldName: "brokerage_name",
  },
  {
    label: "Booking date/time",
    fieldName: "booking_datetime",
  },
]

export const MAX_PAGE_HEIGHT = {
  desktop: "calc(100vh - 250px)",
}
export const CAMPAIGN_OPERATIONS = {
  created: 0,
  updated: 1,
  deleted: 2,
}

export const OFFER_ORDERED_STATUSES = {
  0: "pending",
  1: "sent",
  2: "reviewed",
  3: "accepted",
}

export const OFFER_STATUS_CHOICES = {
  [OFFER_ORDERED_STATUSES[0]]: "Pending",
  [OFFER_ORDERED_STATUSES[1]]: "Sent",
  [OFFER_ORDERED_STATUSES[2]]: "Reviewed",
  [OFFER_ORDERED_STATUSES[3]]: "Accepted",
}

export const TRANSACTION_DIRECTIONS = {
  outgoing: "outgoing",
  incoming: "incoming",
}

/** CRM settings slice from Redux (`selectCRMSettings`). */
export function isCallingPhoneReady(crmSettings, profile) {
  if (isConcierge(profile)) {
    return !!profile?.twilio_voice_phone
  }
  return !!crmSettings?.settings?.twilio_call_phone
}
